/**
 * Template utilities for building Google Fonts URLs, applying style overrides,
 * section management, and calendar link generation.
 */

/**
 * Strip markdown code fences from AI output (```html ... ``` or ``` ... ```).
 */
export function stripCodeFences(text: string): string {
  return text
    .replace(/^```(?:html)?\s*\n?/i, "")
    .replace(/\n?```\s*$/i, "")
    .trim();
}
import {
  TEMPLATE_IDS,
  TEMPLATE_SECTION_IDS,
  type TemplateId,
  type TemplateSectionId,
} from "./templateTypes";

export interface StyleOverrides {
  colorBackground?: string;
  colorBackgroundAlt?: string;
  colorMuted?: string;
  colorPrimary?: string;
  colorSecondary?: string;
  fontBody?: string;
  fontHeading?: string;
}

export type TemplateFontPairing = {
  body: string;
  heading: string;
};

export const TEMPLATE_FONT_PAIRINGS: Array<TemplateFontPairing> = [
  { body: "Lora", heading: "Playfair Display" },
  { body: "Inter", heading: "Montserrat" },
  { body: "Source Sans 3", heading: "Cormorant Garamond" },
  { body: "Open Sans", heading: "Josefin Sans" },
  { body: "Source Sans 3", heading: "Libre Baskerville" },
  { body: "Inter", heading: "Berlingske Serif" },
  { body: "PP Mori", heading: "Jubilee" },
];

export const STYLE_COLOR_KEYS = [
  "colorPrimary",
  "colorSecondary",
  "colorBackground",
  "colorBackgroundAlt",
  "colorMuted",
] as const;

type StyleColorKey = (typeof STYLE_COLOR_KEYS)[number];

const STYLE_COLOR_TO_CSS_VAR: Record<StyleColorKey, string> = {
  colorBackground: "--color-background",
  colorBackgroundAlt: "--color-background-alt",
  colorMuted: "--color-muted",
  colorPrimary: "--color-primary",
  colorSecondary: "--color-secondary",
};

const FONT_SPECS: Record<string, string> = {
  "Cormorant Garamond": "Cormorant+Garamond:ital,wght@0,400;0,600;1,400",
  Inter: "Inter:wght@300;400;500",
  "Josefin Sans": "Josefin+Sans:wght@300;400;600",
  Jubilee: "",
  "Libre Baskerville": "Libre+Baskerville:ital,wght@0,400;0,700;1,400",
  Lora: "Lora:ital,wght@0,400;0,600;1,400",
  Montserrat: "Montserrat:wght@300;400;600;800",
  "Open Sans": "Open+Sans:wght@300;400;600",
  "Playfair Display": "Playfair+Display:ital,wght@0,400;0,700;1,400",
  "PP Mori": "",
  "Source Sans 3": "Source+Sans+3:wght@300;400;600",
};

const LOCAL_FONT_STYLESHEETS: Record<string, string> = {
  Jubilee: "/willardson/fonts.css",
  "PP Mori": "/willardson/fonts.css",
};

const SERIF_FONTS = new Set([
  "Berlingske Serif",
  "Playfair Display",
  "Lora",
  "Cormorant Garamond",
  "Libre Baskerville",
  "Jubilee",
]);

export const TEMPLATE_DEFAULTS: Record<TemplateId, Required<StyleOverrides>> = {
  editorial: {
    colorBackground: "#f2d0b6",
    colorBackgroundAlt: "#ead7c8",
    colorMuted: "#ac7b6b",
    colorPrimary: "#8f403f",
    colorSecondary: "#233b52",
    fontBody: "PP Mori",
    fontHeading: "Jubilee",
  },
  narrative: {
    colorBackground: "#f6f2e9",
    colorBackgroundAlt: "#ece6da",
    colorMuted: "#8b8376",
    colorPrimary: "#1d1b18",
    colorSecondary: "#3f3a33",
    fontBody: "Inter",
    fontHeading: "Berlingske Serif",
  },
  traditional: {
    colorBackground: "#fdfbf5",
    colorBackgroundAlt: "#f5f0e6",
    colorMuted: "#8a7e6e",
    colorPrimary: "#b8860b",
    colorSecondary: "#1c2340",
    fontBody: "Lora",
    fontHeading: "Playfair Display",
  },
  typographic: {
    colorBackground: "#ffffff",
    colorBackgroundAlt: "#f0f0f0",
    colorMuted: "#777777",
    colorPrimary: "#1a1a1a",
    colorSecondary: "#333333",
    fontBody: "Inter",
    fontHeading: "Montserrat",
  },
  watercolor: {
    colorBackground: "#fef9fb",
    colorBackgroundAlt: "#f4ecf0",
    colorMuted: "#9e8fa0",
    colorPrimary: "#c9a0dc",
    colorSecondary: "#4a3f50",
    fontBody: "Source Sans 3",
    fontHeading: "Cormorant Garamond",
  },
  whimsical: {
    colorBackground: "#fef1ec",
    colorBackgroundAlt: "#f9e5dd",
    colorMuted: "#687089",
    colorPrimary: "#ff5734",
    colorSecondary: "#11223f",
    fontBody: "Source Sans 3",
    fontHeading: "Cormorant Garamond",
  },
};

export const TEMPLATE_DIVIDER_CLASS: Record<TemplateId, string> = {
  editorial: "editorial-rule",
  narrative: "editorial-rule",
  traditional: "formal-rule",
  typographic: "typo-dash",
  watercolor: "watercolor-stroke",
  whimsical: "whimsical-divider",
};

export const TEMPLATE_USES_INITIALS = new Set<TemplateId>(["typographic"]);

export const TEMPLATE_WITHOUT_GALLERY_MARQUEE = new Set<TemplateId>([
  "narrative",
  "traditional",
  "typographic",
  "watercolor",
]);

export const TEMPLATE_PERSONALITY: Record<TemplateId, string> = {
  editorial:
    "editorial collage, warm peach canvas, oversized serif type, premium botanical cutouts and animated reveals",
  narrative: "editorial, museum-like, airy, luxurious and narrative driven",
  traditional: "classic formal, rich and regal, very traditional",
  typographic: "bold typography-focused, minimal decoration, creative text",
  watercolor: "dreamy, soft pastels, airy watercolor aesthetic",
  whimsical:
    "whimsical, illustrated botanicals, asymmetrical layouts, romantic but art-directed",
};

export const TEMPLATE_GALLERY_TITLES: Record<TemplateId, string> = {
  editorial: "Nuestra Historia",
  narrative: "Momentos que permanecen",
  traditional: "Nuestra Historia",
  typographic: "Nuestra Historia",
  watercolor: "Nuestra Historia",
  whimsical: "Nuestra Historia",
};

export function normalizeTemplateId(
  templateId: string | null | undefined,
): TemplateId {
  if (templateId && (TEMPLATE_IDS as readonly string[]).includes(templateId)) {
    return templateId as TemplateId;
  }
  return "traditional";
}

export function getTemplateDefaults(
  templateId: TemplateId | string | null | undefined,
): Required<StyleOverrides> {
  return TEMPLATE_DEFAULTS[normalizeTemplateId(templateId)];
}

export function resolveTemplateFonts(
  templateId: TemplateId | string | null | undefined,
  overrides?: StyleOverrides,
): TemplateFontPairing {
  const defaults = getTemplateDefaults(templateId);
  return {
    body: overrides?.fontBody ?? defaults.fontBody,
    heading: overrides?.fontHeading ?? defaults.fontHeading,
  };
}

/**
 * Build a Google Fonts CSS URL for the given heading + body font families.
 * Falls back to the template default URL spec if a family is unknown.
 */
export function buildGoogleFontsUrl(heading: string, body: string): string {
  const localStylesheetUrls = Array.from(
    new Set(
      [heading, body]
        .map((family) => LOCAL_FONT_STYLESHEETS[family])
        .filter(Boolean),
    ),
  ) as Array<string>;

  const headingSpec = FONT_SPECS[heading];
  const bodySpec = FONT_SPECS[body];

  const families: Array<string> = [];
  if (headingSpec) {
    families.push(headingSpec);
  }
  if (bodySpec && bodySpec !== headingSpec) {
    families.push(bodySpec);
  }

  if (families.length === 0 && localStylesheetUrls.length > 0) {
    return localStylesheetUrls[0];
  }

  if (families.length === 0) {
    const defaults = TEMPLATE_DEFAULTS.traditional;
    return buildGoogleFontsUrl(defaults.fontHeading, defaults.fontBody);
  }

  return `https://fonts.googleapis.com/css2?family=${families.join("&family=")}&display=swap`;
}

export function buildTemplateFontsUrl(
  templateId: TemplateId | string | null | undefined,
  overrides?: StyleOverrides,
): string {
  const fonts = resolveTemplateFonts(templateId, overrides);
  return buildGoogleFontsUrl(fonts.heading, fonts.body);
}

function buildFontCssVarValue(family: string): string {
  const fallback = isSerifFont(family) ? "serif" : "sans-serif";
  return `'${family}',${fallback}`;
}

export function buildCssVariables(
  overrides?: StyleOverrides,
): Record<string, string> {
  const cssVars: Record<string, string> = {};
  if (!overrides) {
    return cssVars;
  }

  for (const key of STYLE_COLOR_KEYS) {
    const value = overrides[key];
    if (value) {
      cssVars[STYLE_COLOR_TO_CSS_VAR[key]] = value;
    }
  }

  if (overrides.fontHeading) {
    cssVars["--font-heading"] = buildFontCssVarValue(overrides.fontHeading);
  }
  if (overrides.fontBody) {
    cssVars["--font-body"] = buildFontCssVarValue(overrides.fontBody);
  }

  return cssVars;
}

export function buildResolvedCssVariables(
  templateId: TemplateId | string | null | undefined,
  overrides?: StyleOverrides,
): Record<string, string> {
  const defaults = getTemplateDefaults(templateId);
  return buildCssVariables({
    colorBackground: overrides?.colorBackground ?? defaults.colorBackground,
    colorBackgroundAlt:
      overrides?.colorBackgroundAlt ?? defaults.colorBackgroundAlt,
    colorMuted: overrides?.colorMuted ?? defaults.colorMuted,
    colorPrimary: overrides?.colorPrimary ?? defaults.colorPrimary,
    colorSecondary: overrides?.colorSecondary ?? defaults.colorSecondary,
    fontBody: overrides?.fontBody ?? defaults.fontBody,
    fontHeading: overrides?.fontHeading ?? defaults.fontHeading,
  });
}

export function buildTemplateDefaultCssVars(
  templateId: TemplateId,
  extraVars?: Record<string, string>,
): Record<string, string> {
  return {
    ...buildResolvedCssVariables(templateId),
    ...extraVars,
  };
}

/**
 * Apply style overrides to a rendered HTML string.
 *
 * 1. Replaces {{GOOGLE_FONTS_URL}} with the correct fonts URL.
 * 2. If any color or font overrides exist, injects a CSS `:root` block
 *    right before `</style>` to override CSS custom properties.
 */
export function applyStyleOverrides(
  html: string,
  overrides: StyleOverrides | undefined,
  templateId: TemplateId,
): string {
  // Replace Google Fonts URL placeholder
  html = html.replaceAll(
    "{{GOOGLE_FONTS_URL}}",
    buildTemplateFontsUrl(templateId, overrides),
  );

  // Build CSS variable override block if any overrides are set
  if (!overrides) {
    return html;
  }

  const cssVars = buildCssVariables(overrides);
  const cssOverrides: Array<string> = Object.entries(cssVars).map(
    ([k, v]) => `${k}:${v}`,
  );

  if (cssOverrides.length === 0) {
    return html;
  }

  // Inject override block right before </style>
  const overrideBlock = `\n:root{${cssOverrides.join(";")}}`;
  html = html.replace("</style>", `${overrideBlock}\n</style>`);

  return html;
}

function isSerifFont(family: string): boolean {
  return SERIF_FONTS.has(family);
}

// ---------------------------------------------------------------------------
// Section management
// ---------------------------------------------------------------------------

export type TemplateSectionEntry = {
  enabled: boolean;
  sectionId: TemplateSectionId;
  type: "template";
};

export type CustomSectionEntry = {
  errorMessage?: string;
  generationStatus?: "generating" | "ready" | "error";
  html: string;
  label: string;
  previousHtml?: string;
  sectionId: string;
  type: "custom";
};

export type SectionEntry = TemplateSectionEntry | CustomSectionEntry;

export interface TemplateSectionAvailabilityOptions {
  hasRenderablePhotos?: boolean;
}

export function isTemplateSectionAvailable(
  sectionId: TemplateSectionId,
  options: TemplateSectionAvailabilityOptions = {},
): boolean {
  if (sectionId === "gallery") {
    return options.hasRenderablePhotos !== false;
  }

  return true;
}

/** Build a Google Calendar "add event" URL for a wedding. */
export function buildGoogleCalendarLink(args: {
  brideName: string;
  groomName: string;
  venueAddress?: string | null;
  venueName: string;
  weddingDate: string;
  weddingTime?: string | null;
}): string {
  const title = encodeURIComponent(
    `Boda de ${args.brideName} & ${args.groomName}`,
  );
  const location = encodeURIComponent(
    [args.venueName, args.venueAddress].filter(Boolean).join(", "),
  );

  // Parse date: expecting YYYY-MM-DD
  const dateParts = args.weddingDate.split("-");
  const dateStr = dateParts.join("");

  let dates: string;
  if (args.weddingTime) {
    const timeStr = args.weddingTime.replace(":", "") + "00";
    const startDate = `${dateStr}T${timeStr}`;
    const start = new Date(`${args.weddingDate}T${args.weddingTime}:00`);
    start.setHours(start.getHours() + 4);
    const endH = String(start.getHours()).padStart(2, "0");
    const endM = String(start.getMinutes()).padStart(2, "0");
    const endDate = `${dateStr}T${endH}${endM}00`;
    dates = `${startDate}/${endDate}`;
  } else {
    dates = `${dateStr}/${dateStr}`;
  }

  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&location=${location}`;
}

/** Returns the default section config: all template sections enabled in canonical order. */
export function getDefaultSectionConfig(): Array<SectionEntry> {
  return TEMPLATE_SECTION_IDS.map((id) => ({
    enabled: true,
    sectionId: id,
    type: "template" as const,
  }));
}

// ---------------------------------------------------------------------------
// HTML section parsing and reordering
// ---------------------------------------------------------------------------

/**
 * Parse the section comment markers in an HTML string.
 * Returns prefix (head+CSS up to first marker), ordered sections, and suffix (scripts+close tags).
 */
function parseSectionedHtml(html: string): {
  prefix: string;
  sections: Array<{ html: string; id: string }>;
  suffix: string;
} {
  const regex =
    /<!-- bodaweb:section:([\w-]+) -->([\S\s]*?)<!-- \/bodaweb:section:\1 -->/g;

  const rawSections: Array<{
    end: number;
    full: string;
    id: string;
    start: number;
  }> = [];

  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    rawSections.push({
      end: match.index + match[0].length,
      full: match[0],
      id: match[1],
      start: match.index,
    });
  }

  if (rawSections.length === 0) {
    return { prefix: html, sections: [], suffix: "" };
  }

  const prefix = html.slice(0, rawSections[0].start);
  const suffix = html.slice(rawSections.at(-1)!.end);
  const sections = rawSections.map((s) => ({ html: s.full, id: s.id }));

  return { prefix, sections, suffix };
}

/**
 * Reassemble HTML according to sectionConfig order and visibility.
 * Custom sections with generationStatus !== "ready" are skipped.
 * If sectionConfig is undefined, returns html unchanged.
 */
export function reorderSections(
  html: string,
  config: Array<SectionEntry>,
): string {
  const { prefix, sections, suffix } = parseSectionedHtml(html);
  const sectionMap = new Map(sections.map((s) => [s.id, s.html]));

  let body = "";
  for (const entry of config) {
    if (entry.type === "template") {
      if (!entry.enabled) {
        continue;
      }
      const block = sectionMap.get(entry.sectionId);
      if (block) {
        // Inject data attributes on the first HTML element for edit mode identification
        const markedBlock = block.replace(
          /(<(?:section|div|footer|header|nav|main|article|aside)\b)([^>]*>)/i,
          `$1 data-section-type="template" data-section-id="${entry.sectionId}"$2`,
        );
        body += markedBlock + "\n";
      }
    } else if (entry.type === "custom" && entry.generationStatus === "ready") {
      // Inject data attributes on the root <section> element for edit mode identification
      const markedHtml = entry.html.replace(
        /^(<section)(\s|>)/i,
        `$1 data-section-type="custom" data-section-id="${entry.sectionId}"$2`,
      );
      body += `<!-- bodaweb:section:${entry.sectionId} -->\n${markedHtml}\n<!-- /bodaweb:section:${entry.sectionId} -->\n`;
    }
  }

  return prefix + body + suffix;
}
